import axios from "axios";
import { useMemo, useRef, useState } from "react";
import styled from "styled-components";
import API_ENDPOINTS from "../config/api";

const Container = styled.main`
  min-height: 100dvh;
  width: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #013566;
  margin: 0 0 1rem 0;
  text-align: center;
`;

const ActionsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #eee;
  flex-wrap: wrap;
`;

const ActionsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const UploadButton = styled.button<{ $disabled?: boolean }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  background-color: ${(p) => (p.$disabled ? "#9aa9b7" : "#013566")};
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #012a4a;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const SecondaryButton = styled.button<{ $disabled?: boolean }>`
  padding: 0.75rem 1.25rem;
  border: 1px solid #013566;
  border-radius: 8px;
  background-color: ${(p) => (p.$disabled ? "#f8f9fa" : "transparent")};
  color: ${(p) => (p.$disabled ? "#6c757d" : "#013566")};
  font-size: 1rem;
  font-weight: 600;
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #eef3f8;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const ColumnsPanel = styled.div`
  width: 100%;
  background: white;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ColumnsPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const ColumnsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(220px, 1fr));
  gap: 0.5rem 1rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const ColumnToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  color: #333;
  user-select: none;
`;

const Tile = styled.div`
  border: 2px solid #eee;
  border-radius: 12px;
  -webkit-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  -moz-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
`;

const TableScroller = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
`;

const TableInner = styled.div`
  display: inline-block;
  min-width: 100%;
  width: max-content;
`;

const TileHeader = styled.div`
  width: 100%;
  display: grid;
  gap: 1rem;
  align-items: center;
  background: #f8f9fa;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #eee;

  h2 {
    font-size: 1.2rem;
    font-weight: 600;
    color: #013566;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const TableBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Row = styled.div`
  display: grid;
  gap: 1rem;
  align-items: center;
  border-bottom: 1px solid #eee;
  padding: 12px 0;

  &:last-child {
    border-bottom: none;
  }
`;

const Cell = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ExpandButton = styled.button<{ $isOpen?: boolean }>`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid #ddd;
  background: ${(p) => (p.$isOpen ? "#eef3f8" : "white")};
  cursor: pointer;
  color: #013566;
  font-weight: 800;
  line-height: 1;

  &:hover {
    border-color: #013566;
  }
`;

const ExpandedPanel = styled.div`
  grid-column: 1 / -1;
  background: #f8f9fa;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 1rem;
  display: grid;
  grid-template-columns: repeat(3, minmax(220px, 1fr));
  gap: 0.75rem 1rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const KV = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

const K = styled.div`
  font-size: 0.85rem;
  font-weight: 700;
  color: #013566;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const V = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SkeletonRow = styled.div`
  display: grid;
  gap: 1rem;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }
`;

const Skeleton = styled.div`
  height: 18px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  border-radius: 4px;
  animation: loading 1.5s ease-in-out infinite;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const EmptyState = styled.div`
  padding: 2rem 0;
  text-align: center;
  color: #6c757d;
  font-weight: 500;
`;

const ACCEPTED_EXTENSIONS = [
  ".xlsx",
  ".xls",
  ".csv",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "text/csv",
].join(",");

type ReportRow = {
  period: string;
  period_start: string | null;
  period_end: string | null;
  total_leads: number;
  lead_cost: number;
  cpl_rate: number;
  appointments: number;
  appointment_rate_pct: number | null;
  gross_sales: number;
  net_sales: number;
  sale_count: number;
  sale_rate_pct: number | null;
  client_com_pct: number;
  actual_com: number;
  total_revenue: number;
  margin: number;
};

type ColumnKey = keyof ReportRow;

const COLUMNS: Array<{
  key: ColumnKey;
  label: string;
  defaultVisible: boolean;
  format?: "text" | "number" | "money" | "pct";
}> = [
  { key: "period", label: "Period", defaultVisible: true, format: "text" },
  { key: "period_start", label: "Period start", defaultVisible: false, format: "text" },
  { key: "period_end", label: "Period end", defaultVisible: false, format: "text" },
  { key: "total_leads", label: "Total leads", defaultVisible: true, format: "number" },
  { key: "lead_cost", label: "Lead cost", defaultVisible: true, format: "money" },
  { key: "cpl_rate", label: "CPL rate", defaultVisible: false, format: "money" },
  { key: "appointments", label: "Appointments", defaultVisible: false, format: "number" },
  { key: "appointment_rate_pct", label: "Appt rate", defaultVisible: false, format: "pct" },
  { key: "gross_sales", label: "Gross sales", defaultVisible: true, format: "money" },
  { key: "net_sales", label: "Net sales", defaultVisible: false, format: "money" },
  { key: "sale_count", label: "Sale count", defaultVisible: false, format: "number" },
  { key: "sale_rate_pct", label: "Sale rate", defaultVisible: false, format: "pct" },
  { key: "client_com_pct", label: "Client COM %", defaultVisible: false, format: "pct" },
  { key: "actual_com", label: "Actual COM", defaultVisible: false, format: "money" },
  { key: "total_revenue", label: "Total revenue", defaultVisible: false, format: "money" },
  { key: "margin", label: "Margin", defaultVisible: true, format: "money" },
];

const Reporting = () => {
  const token = useMemo(() => localStorage.getItem("token"), []);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [expandedPeriods, setExpandedPeriods] = useState<Record<string, boolean>>(
    {},
  );
  const [showColumnsPanel, setShowColumnsPanel] = useState(false);
  const [visibleCols, setVisibleCols] = useState<Record<ColumnKey, boolean>>(() => {
    const initial = {} as Record<ColumnKey, boolean>;
    for (const c of COLUMNS) initial[c.key] = c.defaultVisible;
    return initial;
  });

  const visibleColumnDefs = COLUMNS.filter((c) => visibleCols[c.key]);
  const hiddenColumnDefs = COLUMNS.filter((c) => !visibleCols[c.key]);
  // Use fixed column widths so horizontal scrolling can always reveal all columns.
  const gridTemplate = `44px repeat(${visibleColumnDefs.length}, 200px)`;

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const formatCell = (colKey: ColumnKey, value: unknown) => {
    if (value === null || value === undefined || value === "") return "N/A";
    const def = COLUMNS.find((c) => c.key === colKey);
    const fmt = def?.format ?? "text";
    if (fmt === "text") return String(value);
    if (typeof value !== "number") return String(value);
    if (fmt === "number") return value.toLocaleString();
    if (fmt === "money")
      return value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    if (fmt === "pct")
      return `${value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      })}%`;
    return String(value);
  };

  const handleDownloadCsv = async () => {
    if (!token) return;
    try {
      setIsDownloading(true);
      const resp = await axios.get(API_ENDPOINTS.REPORT_MRG_IQ_PERFORMANCE, {
        params: {
          output_type: "csv",
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      });

      console.log("MRG iQ performance CSV response:", resp);

      const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `mrg-iq-performance-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Error downloading CSV:", e);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileSelected = async (file: File | null) => {
    if (!file || !token) return;
    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append("file", file);

      const uploadResp = await axios.post(
        API_ENDPOINTS.LEADS_DISPOSITION_UPLOAD,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      console.log("Upload response:", uploadResp.data);

      setTableLoading(true);
      window.setTimeout(async () => {
        try {
          const reportResp = await axios.get(
            API_ENDPOINTS.REPORT_MRG_IQ_PERFORMANCE,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );
          console.log("MRG iQ performance response:", reportResp.data);
          setRows(reportResp.data?.rows || []);
          setExpandedPeriods({});
        } catch (e) {
          console.error("Error fetching report:", e);
        } finally {
          setTableLoading(false);
        }
      }, 5000);
    } catch (e) {
      console.error("Error uploading file:", e);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <Container>
      <PageTitle>Reporting</PageTitle>

      <ActionsBar>
        <ActionsGroup>
          <UploadButton
            type="button"
            onClick={handleUploadClick}
            disabled={!token || isUploading}
            $disabled={!token || isUploading}
          >
            {isUploading ? "Uploading..." : "Upload file"}
          </UploadButton>

          {rows.length > 0 && !tableLoading && (
            <UploadButton
              type="button"
              onClick={handleDownloadCsv}
              disabled={!token || isDownloading}
              $disabled={!token || isDownloading}
            >
              {isDownloading ? "Downloading..." : "Download"}
            </UploadButton>
          )}

          <SecondaryButton
            type="button"
            onClick={() => setShowColumnsPanel((v) => !v)}
            disabled={tableLoading}
            $disabled={tableLoading}
          >
            Columns
          </SecondaryButton>
        </ActionsGroup>

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS}
          style={{ display: "none" }}
          onChange={(e) => handleFileSelected(e.target.files?.[0] ?? null)}
        />
      </ActionsBar>

      {showColumnsPanel && (
        <ColumnsPanel>
          <ColumnsPanelHeader>
            <div style={{ fontWeight: 700, color: "#013566" }}>Show / hide columns</div>
            <ActionsGroup>
              <SecondaryButton
                type="button"
                onClick={() =>
                  setVisibleCols(() => {
                    const next = {} as Record<ColumnKey, boolean>;
                    for (const c of COLUMNS) next[c.key] = true;
                    return next;
                  })
                }
              >
                Show all
              </SecondaryButton>
              <SecondaryButton
                type="button"
                onClick={() =>
                  setVisibleCols(() => {
                    const next = {} as Record<ColumnKey, boolean>;
                    for (const c of COLUMNS) next[c.key] = c.key === "period";
                    return next;
                  })
                }
              >
                Hide all
              </SecondaryButton>
            </ActionsGroup>
          </ColumnsPanelHeader>

          <ColumnsGrid>
            {COLUMNS.map((c) => (
              <ColumnToggle key={c.key}>
                <input
                  type="checkbox"
                  checked={!!visibleCols[c.key]}
                  disabled={c.key === "period"}
                  onChange={() =>
                    setVisibleCols((prev) => ({
                      ...prev,
                      [c.key]: !prev[c.key],
                    }))
                  }
                />
                <span>{c.label}</span>
              </ColumnToggle>
            ))}
          </ColumnsGrid>
        </ColumnsPanel>
      )}

      <Tile>
        <TableScroller>
          <TableInner>
            <TileHeader style={{ gridTemplateColumns: gridTemplate }}>
              <div />
              {visibleColumnDefs.map((c) => (
                <h2 key={c.key}>{c.label}</h2>
              ))}
            </TileHeader>

            <TableBody>
              {tableLoading ? (
                Array.from({ length: 6 }).map((_, idx) => (
                  <SkeletonRow key={idx} style={{ gridTemplateColumns: gridTemplate }}>
                    <Skeleton />
                    {visibleColumnDefs.map((c) => (
                      <Skeleton key={c.key} />
                    ))}
                  </SkeletonRow>
                ))
              ) : rows.length === 0 ? (
                <EmptyState>No data yet</EmptyState>
              ) : (
                rows.map((r) => {
                  const isTotal = r.period === "total";
                  const isOpen = !!expandedPeriods[r.period];
                  return (
                    <div key={r.period}>
                      <Row
                        style={{
                          gridTemplateColumns: gridTemplate,
                          fontWeight: isTotal ? 800 : undefined,
                        }}
                      >
                        <ExpandButton
                          type="button"
                          aria-label={isOpen ? "Collapse row" : "Expand row"}
                          $isOpen={isOpen}
                          onClick={() =>
                            setExpandedPeriods((prev) => ({
                              ...prev,
                              [r.period]: !prev[r.period],
                            }))
                          }
                        >
                          {isOpen ? "−" : "+"}
                        </ExpandButton>

                        {visibleColumnDefs.map((c) => (
                          <Cell
                            key={c.key}
                            style={{
                              fontWeight: isTotal ? 800 : undefined,
                              color: isTotal ? "#013566" : undefined,
                            }}
                          >
                            {formatCell(c.key, r[c.key])}
                          </Cell>
                        ))}

                        {isOpen && (
                          <ExpandedPanel>
                            {hiddenColumnDefs.length === 0 ? (
                              <div style={{ color: "#6c757d", fontWeight: 600 }}>
                                No hidden columns.
                              </div>
                            ) : (
                              hiddenColumnDefs.map((c) => (
                                <KV key={c.key}>
                                  <K>{c.label}</K>
                                  <V>{formatCell(c.key, r[c.key])}</V>
                                </KV>
                              ))
                            )}
                          </ExpandedPanel>
                        )}
                      </Row>
                    </div>
                  );
                })
              )}
            </TableBody>
          </TableInner>
        </TableScroller>
      </Tile>
    </Container>
  );
};

export default Reporting;

